import { RSAA } from 'redux-api-middleware';
import { createSelector, OutputSelector } from 'reselect';
import getCustomersURL from 'url-constructors/getCustomersURL';
import { FeedTypes } from 'topics';
import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { TGetRootState, TDispatchResult } from 'types/shared';

export interface ICustomer {
  color: string;
  customerFeedType: string;
  displayName: string;
  feedType: string;
  id: number;
  isActive: boolean;
  name: string;
  isDeprecated?: boolean;
  thumbnailUrl?: string;
  vectorImageUrl?: string;
}

export const REQUEST = 'opus/customers/REQUEST';
export const SUCCESS = 'opus/customers/SUCCESS';
export const FAILURE = 'opus/customers/FAILURE';

type CustomersActionTypes = typeof REQUEST | typeof SUCCESS | typeof FAILURE;

interface ICustomersAction {
  type: CustomersActionTypes;
  payload: any;
}

export interface ICustomersState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  reason: any;
  data: ICustomer[];
}

export const initialState: ICustomersState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  reason: null,
  data: [],
};

function reducer(
  state: ICustomersState = initialState,
  action: ICustomersAction
): ICustomersState {
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        pending: true,
      };
    case SUCCESS:
      return {
        ...state,
        fulfilled: true,
        pending: false,
        rejected: false,
        reason: null,
        data: action.payload,
      };
    case FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
        reason: action.payload,
      };
    default:
      return state;
  }
}

/**
 * Load customer data from the '/api/customers' endpoint.
 *
 * @see https://github.com/agraboso/redux-api-middleware
 */
function loadCustomers(): TDispatchResult<any> {
  return (dispatch: Dispatch, getState: TGetRootState): any => {
    const { customers } = getState();

    return dispatch({
      [RSAA]: {
        bailout: customers.pending || customers.fulfilled,
        types: [
          REQUEST,
          {
            type: SUCCESS,
            payload: async function payload(
              action: ICustomersAction,
              state: ICustomersState,
              res: any
            ): Promise<any> {
              const json = await res.json();
              const customers = json.customers.map(feedTypeToLowerCase);

              function feedTypeToLowerCase(customer: any): any {
                return {
                  ...customer,
                  feedType: customer.feedType.toLowerCase(),
                  displayName:
                    customer.feedType === 'Downloads' &&
                    customer.name === 'apple'
                      ? 'Apple - iTunes'
                      : customer.displayName,
                };
              }

              return customers;
            },
          },
          FAILURE,
        ],
        endpoint: getCustomersURL(),
        method: 'GET',
        credentials: 'same-origin',
      },
    });
  };
}

function shouldLoadCustomers(): TDispatchResult<boolean> {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const { customers } = getState();

    /**
     * If we have don't have data OR we aren't loading, we should load the customers.
     */
    return !customers.pending || customers.data.length === 0;
  };
}

const selectAllCustomers = (state: RootState): ICustomer[] =>
  state.customers.data;

const isCustomersLoaded = (state: RootState): boolean =>
  state.customers.fulfilled || state.customers.rejected;

/**
 * Returns a single or multiple types of customers for specified feed types.
 * @param feedType Either a single feed type as a string ('streaming'), or an array of feed types (['streaming', 'downloads'])
 */
const selectCustomersByFeedType = (
  feedType: FeedTypes | FeedTypes[]
): OutputSelector<any, any, any> => {
  const normalizedFeedType = Array.isArray(feedType)
    ? feedType.map((type) => (type.includes('download') ? 'downloads' : type))
    : feedType.includes('download')
    ? 'downloads'
    : feedType;

  return createSelector(selectAllCustomers, (customers) =>
    customers.filter(byFeedType(normalizedFeedType))
  );
};

function byFeedType(feedType: FeedTypes | FeedTypes[]) {
  return (customer: any): boolean => feedType.includes(customer.feedType);
}

const selectSocialStreamCustomers = (): OutputSelector<any, any, any> =>
  createSelector(selectAllCustomers, (customers) =>
    customers.filter(
      (customer: any): boolean =>
        customer.feedType === 'social streams' &&
        customer.feedTypes.includes('Creations') &&
        customer.feedTypes.includes('Views')
    )
  );

const selectChartsCustomers = selectCustomersByFeedType('charts');
const selectDownloadCustomers = selectCustomersByFeedType('downloads');
const selectStreamingCustomers = selectCustomersByFeedType('streaming');
const selectSocialStreamPlatforms = selectSocialStreamCustomers();

export {
  isCustomersLoaded,
  loadCustomers,
  shouldLoadCustomers,
  selectAllCustomers,
  selectChartsCustomers,
  selectDownloadCustomers,
  selectStreamingCustomers,
  selectCustomersByFeedType,
  selectSocialStreamPlatforms,
};

export default reducer;
